import { useMemo } from 'react';
import { useClient } from 'urql';
import { fromPromise } from 'xstate5';
import { OrderPaymentStatus } from '@sg/graphql-schema';
import { OrderTypeValues } from '@sg/shared-be/features/ordering';

import {
  checkIfFailureWasAlreadyShown,
  type FetchOrderPaymentStateParams,
  type FetchOrderPaymentStateResponse,
  getLocalStorageOrderId,
} from '@order/features/ordering';

import {
  BagPaymentStateDocument,
  type BagPaymentStateQuery,
} from '../../GraphQL/BagPaymentState.generated';

export const useFetchOrderPaymentState = () => {
  const client = useClient();

  const fetchOrderPaymentState = useMemo(
    () =>
      fromPromise<FetchOrderPaymentStateResponse, FetchOrderPaymentStateParams>(
        async ({ input }) => {
          const orderIdFromInput = input?.orderId;
          const orderIdFromStorage = await getLocalStorageOrderId();
          const orderId = orderIdFromInput || orderIdFromStorage;

          const query = client.query<BagPaymentStateQuery>;

          if (!orderId) throw new Error('Order ID is required');

          const response = await query(
            BagPaymentStateDocument,
            { orderId, orderType },
            { requestPolicy },
          ).toPromise();

          const maybeData = response.data?.orderPaymentState;
          const maybeUpdatedAt = Number(maybeData?.updatedAt);

          const paymentIntentId = maybeData?.paymentIntentId;
          const isFailure = maybeData?.status === OrderPaymentStatus.Failed || OrderPaymentStatus.Released || Boolean(maybeData?.error); // prettier-ignore
          const hasAlreadyShownFailure = isFailure ? await checkIfFailureWasAlreadyShown(paymentIntentId) : false; // prettier-ignore

          const paymentStateExpiration = Number.isNaN(maybeUpdatedAt) ? undefined : maybeUpdatedAt; // prettier-ignore
          const paymentStateStatus = hasAlreadyShownFailure ? OrderPaymentStatus.Released : maybeData?.status; // prettier-ignore
          const paymentStateError = hasAlreadyShownFailure ? undefined : maybeData?.error; // prettier-ignore

          return {
            order: { id: orderId, hasEarnedLoyaltyPoints: false },
            paymentIntentId,
            paymentStateExpiration,
            paymentStateStatus,
            paymentStateError,
          };
        },
      ),
    [client.query],
  );

  return { fetchOrderPaymentState };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const requestPolicy = 'network-only' as const;
const orderType = OrderTypeValues.GravyOrder;
